/** Seeded PRNG (mulberry32). */
function createRng(seed: number): () => number {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export type WordSearchFillMode = 'english' | 'farsi' | 'mixed';

const FILL_ENGLISH = 'abcdefghijklmnopqrstuvwxyz';
const FILL_FARSI = 'ابپتثجچحخدذرزژسشصضطظعغفقکگلمنوهی';
const FILL_MIXED = FILL_FARSI + FILL_ENGLISH;

export function resolveWordSearchFillAlphabet(mode?: WordSearchFillMode | string | null): string {
  const m = String(mode ?? 'mixed').toLowerCase();
  if (m === 'english') return FILL_ENGLISH;
  if (m === 'farsi' || m === 'persian') return FILL_FARSI;
  return FILL_MIXED;
}

/** Recommended / hard max cols for Find the Words & Crossword (mobile-friendly). */
export const FIND_THE_WORDS_MOBILE_MAX_SIZE = 10;
export const INTERACTIVE_GRID_MAX_COLS = 10;

type DirectionKey = 'E' | 'W' | 'S' | 'N' | 'SE' | 'SW' | 'NE' | 'NW';

const DIRECTION_DELTAS: Record<DirectionKey, { dr: number; dc: number }> = {
  E: { dr: 0, dc: 1 },
  W: { dr: 0, dc: -1 },
  S: { dr: 1, dc: 0 },
  N: { dr: -1, dc: 0 },
  SE: { dr: 1, dc: 1 },
  SW: { dr: 1, dc: -1 },
  NE: { dr: -1, dc: 1 },
  NW: { dr: -1, dc: -1 },
};

function pickDirections(opts: {
  horizontal?: boolean;
  vertical?: boolean;
  diagonal?: boolean;
}): DirectionKey[] {
  const horizontal = opts.horizontal !== false;
  const vertical = opts.vertical !== false;
  const diagonal = opts.diagonal === true;
  const dirs: DirectionKey[] = [];
  if (horizontal) dirs.push('E', 'W');
  if (vertical) dirs.push('S', 'N');
  if (diagonal) dirs.push('SE', 'SW', 'NE', 'NW');
  return dirs.length ? dirs : ['E'];
}

function canPlace(
  grid: (string | null)[][],
  word: string,
  row: number,
  col: number,
  dr: number,
  dc: number,
  rows: number,
  cols: number
): boolean {
  for (let i = 0; i < word.length; i++) {
    const r = row + i * dr;
    const c = col + i * dc;
    if (r < 0 || r >= rows || c < 0 || c >= cols) return false;
    const existing = grid[r][c];
    if (existing != null && existing !== word[i]) return false;
  }
  return true;
}

function placeWord(
  grid: (string | null)[][],
  word: string,
  row: number,
  col: number,
  dr: number,
  dc: number
): void {
  for (let i = 0; i < word.length; i++) {
    grid[row + i * dr][col + i * dc] = word[i];
  }
}

export interface WordSearchPlacement {
  word: string;
  row: number;
  col: number;
  direction: string;
}

/**
 * Places words randomly on a grid (seeded RNG). Unplaced words are skipped.
 * Remaining cells are filled from the selected alphabet (english / farsi / mixed).
 */
export function generateWordSearchGrid(
  words: string[],
  rows: number,
  cols: number,
  opts: {
    horizontal?: boolean;
    vertical?: boolean;
    diagonal?: boolean;
    seed?: number;
    fillMode?: WordSearchFillMode | string | null;
  } = {}
): { gridLetters: string; placements: WordSearchPlacement[] } {
  const safeRows = Math.max(1, rows | 0);
  const safeCols = Math.max(1, cols | 0);
  const rng = createRng(opts.seed ?? Date.now());
  const fillAlphabet = resolveWordSearchFillAlphabet(opts.fillMode);
  const dirs = pickDirections(opts);
  const grid: (string | null)[][] = Array.from({ length: safeRows }, () =>
    Array.from({ length: safeCols }, () => null)
  );
  const placements: WordSearchPlacement[] = [];

  const cleaned = words
    .map((w) => String(w ?? '').replace(/\s+/g, '').trim())
    .filter((w) => w.length > 0)
    .sort((a, b) => b.length - a.length);

  for (const word of cleaned) {
    if (word.length > Math.max(safeRows, safeCols)) continue;
    let placed = false;
    const maxAttempts = Math.max(40, safeRows * safeCols * 2);
    for (let attempt = 0; attempt < maxAttempts && !placed; attempt++) {
      const dirKey = dirs[Math.floor(rng() * dirs.length)]!;
      const { dr, dc } = DIRECTION_DELTAS[dirKey];
      const row = Math.floor(rng() * safeRows);
      const col = Math.floor(rng() * safeCols);
      if (!canPlace(grid, word, row, col, dr, dc, safeRows, safeCols)) continue;
      placeWord(grid, word, row, col, dr, dc);
      placements.push({ word, row, col, direction: dirKey });
      placed = true;
    }
  }

  let gridLetters = '';
  for (let r = 0; r < safeRows; r++) {
    for (let c = 0; c < safeCols; c++) {
      const ch = grid[r][c];
      if (ch != null) {
        gridLetters += ch;
      } else {
        gridLetters += fillAlphabet[Math.floor(rng() * fillAlphabet.length)]!;
      }
    }
  }

  return { gridLetters, placements };
}

/** Read letters along a path of cells (row-major gridLetters). */
export function getWordAtPath(
  gridLetters: string,
  rows: number,
  cols: number,
  cells: Array<{ r: number; c: number }>
): string {
  if (!cells.length || rows <= 0 || cols <= 0) return '';
  let out = '';
  for (const cell of cells) {
    if (cell.r < 0 || cell.r >= rows || cell.c < 0 || cell.c >= cols) return '';
    const idx = cell.r * cols + cell.c;
    if (idx < 0 || idx >= gridLetters.length) return '';
    out += gridLetters[idx];
  }
  return out;
}

const ALL_SEARCH_DIRS = Object.values(DIRECTION_DELTAS);

/**
 * Locate `word` in a flat row-major letter grid by searching all 8 directions.
 * Returns the cell path (start → end) or null if not found.
 */
export function findWordPathInGrid(
  gridLetters: string,
  rows: number,
  cols: number,
  word: string
): Array<{ r: number; c: number }> | null {
  const target = String(word ?? '')
    .replace(/\s+/g, '')
    .trim()
    .toLocaleLowerCase();
  if (!target || rows <= 0 || cols <= 0 || !gridLetters) return null;

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      for (const { dr, dc } of ALL_SEARCH_DIRS) {
        const path: Array<{ r: number; c: number }> = [];
        let match = true;
        for (let i = 0; i < target.length; i++) {
          const rr = r + i * dr;
          const cc = c + i * dc;
          if (rr < 0 || rr >= rows || cc < 0 || cc >= cols) {
            match = false;
            break;
          }
          const idx = rr * cols + cc;
          const ch = (gridLetters[idx] ?? '').toLocaleLowerCase();
          if (ch !== target[i]) {
            match = false;
            break;
          }
          path.push({ r: rr, c: cc });
        }
        if (match) return path;
      }
    }
  }
  return null;
}
